/**
 * The signing page (`#/sign/<token>`) and the calls it makes.
 *
 * Read at module load, like a payment link (usePaymentLink.ts), because it
 * decides what the app is before anything else starts: a signer is not a user
 * of this portal, has no Lark session and may never have heard of AutomaDoc.
 * So the page renders ahead of the Lark gate and authentication is never
 * started. Nothing on this page may record its URL: the token in it *is* the
 * credential.
 *
 * In the page's own URL the token sits in the fragment, which a browser sends
 * to no server and puts in no Referer — so the link does not leak through the
 * portal's host or anything this page links to. The API calls carry it in
 * their path, to our own API only. It is left in place (unlike `_ptxn`) so
 * that reloading the page, or coming back to it later, still works.
 */
import type {
  DeclineDocumentBody,
  SignDocumentBody,
  SignDocumentResponse,
  SigningLinkInfo,
  UnlockSigningLinkResponse,
} from '@lark-automadoc/shared-types'
import { createEndpoints } from '@lark-automadoc/shared-types'
import { hashChangeNeedsReload, signingTokenOf } from './signingLinkHash'

/** The link token when this page load is a signing link, else null. */
export const signingToken: string | null = signingTokenOf(window.location.hash)

// A link pasted into an open tab of this origin changes only the fragment, and the page
// is a signing page or not, for one link, from its load (see hashChangeNeedsReload). So a
// change to or from a signing link, or to another one, loads the page again at the new
// address. The address is put back first: in a portal tab the router may already have
// redirected it to the dashboard.
window.addEventListener('hashchange', (event) => {
  if (!hashChangeNeedsReload(signingToken, event.newURL)) return
  window.history.replaceState(window.history.state, '', event.newURL)
  window.location.reload()
})

const endpoints = createEndpoints(import.meta.env.VITE_API_URL || '')

/** A refusal from the signing API, carrying the code the page translates. */
export class SigningApiError extends Error {
  constructor(
    readonly code: string | undefined,
    readonly status: number,
    readonly body: Record<string, unknown>,
  ) {
    super(code ?? `HTTP ${status}`)
  }
}

/**
 * Plain fetch, deliberately not the portal's apiRequest: that one needs a
 * signed-in user, and wraps every call in a trace span named after its path —
 * which here would carry the token into the traces.
 */
async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init.headers ?? {}) },
    credentials: 'omit',
    referrerPolicy: 'no-referrer',
  })
  const body = (await response.json().catch(() => ({}))) as Record<string, unknown>
  if (!response.ok) throw new SigningApiError(body.code as string | undefined, response.status, body)
  return body as T
}

export function signingApi(token: string) {
  const base = endpoints.signingLink(token)
  return {
    info(session?: string | null): Promise<SigningLinkInfo> {
      return call(session ? `${base}?session=${encodeURIComponent(session)}` : base)
    },
    unlock(password: string): Promise<UnlockSigningLinkResponse> {
      return call(`${base}/unlock`, { method: 'POST', body: JSON.stringify({ password }) })
    },
    sign(body: SignDocumentBody): Promise<SignDocumentResponse> {
      return call(`${base}/sign`, { method: 'POST', body: JSON.stringify(body) })
    },
    decline(body: DeclineDocumentBody): Promise<{ success: boolean }> {
      return call(`${base}/decline`, { method: 'POST', body: JSON.stringify(body) })
    },
    /** The PDF's own URL: the src of the viewer, and what "Open" and "Download" point at. */
    documentUrl(session: string | null, download = false): string {
      const query = new URLSearchParams()
      if (session) query.set('session', session)
      if (download) query.set('download', '1')
      const qs = query.toString()
      return `${base}/document${qs ? `?${qs}` : ''}`
    },
  }
}
